'use client';

import type { MouseEvent, ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { LinkSquare02Icon } from '@terra/ui-icons';
import { Badge } from '../../../../../components/badge';
import { Link } from '../../../../../components/link';
import { Text } from '../../../../../components/styled/text';
import { MarkdownPreview } from '../../../../markdown-preview';
import { JSONRenderer } from '../../ToolCallsRenderers/renderers/JSONRenderer';
import { PlainTextRenderer } from '../../ToolCallsRenderers/renderers/PlainTextRenderer';
import type { OutputRenderContext } from '../../tool-config-types';

type DocsSearchResultItem = {
  rank: number;
  slug: string;
  title: string;
  section: string;
  headingPath: string | null;
  url: string;
  excerpt?: string;
  content?: string;
};
type DocsSearchToolOutput = {
  query: string;
  total: number;
  results: DocsSearchResultItem[];
};
type WindowWithElectronShell = Window & {
  electronAPI?: {
    shell?: {
      openExternal?: (url: string) => Promise<{
        success: boolean;
      }>;
    };
  };
};

function isDocsSearchResultItem(value: unknown): value is DocsSearchResultItem {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const item = value as Record<string, unknown>;

  return (
    typeof item.rank === 'number' &&
    typeof item.slug === 'string' &&
    typeof item.title === 'string' &&
    typeof item.section === 'string' &&
    typeof item.url === 'string'
  );
}

function isDocsSearchToolOutput(value: unknown): value is DocsSearchToolOutput {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const output = value as Record<string, unknown>;

  return (
    typeof output.query === 'string' &&
    typeof output.total === 'number' &&
    Array.isArray(output.results) &&
    output.results.every(isDocsSearchResultItem)
  );
}

function parseDocsOutput(content: unknown): DocsSearchToolOutput | null {
  if (isDocsSearchToolOutput(content)) {
    return content;
  }

  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content) as unknown;

      if (isDocsSearchToolOutput(parsed)) {
        return parsed;
      }

      if (Array.isArray(parsed) && parsed.every(isDocsSearchResultItem)) {
        return {
          query: '',
          total: parsed.length,
          results: parsed,
        };
      }
    } catch {
      return null;
    }
  }

  return null;
}

function resolveDocsLink(url: string): string | null {
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return null;
  }

  const currentOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  const webAppOrigin =
    currentOrigin === 'http://localhost:4002'
      ? 'http://localhost:4200'
      : currentOrigin === 'http://127.0.0.1:4002'
        ? 'http://127.0.0.1:4200'
        : currentOrigin === 'https://tester.staging.terra.security'
          ? 'https://app.staging.terra.security'
          : currentOrigin === 'https://tester.terra.security'
            ? 'https://app.terra.security'
            : currentOrigin;

  if (trimmedUrl.startsWith('/docs/')) {
    if (!webAppOrigin) {
      return trimmedUrl;
    }

    try {
      return new URL(trimmedUrl, webAppOrigin).toString();
    } catch {
      return null;
    }
  }

  try {
    const parsed = new URL(trimmedUrl);

    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.toString();
    }
  } catch {
    return null;
  }

  return null;
}

function getExcerpt(item: DocsSearchResultItem): string {
  const source = (item.excerpt ?? item.content ?? '').trim();

  if (source.length <= 420) {
    return source;
  }

  return `${source.slice(0, 420)}...`;
}

function stripMarkdownToPlainText(value: string): string {
  return value
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/[*_~]/g, '')
    .replace(/\|/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function shouldRenderAsPlainText(value: string): boolean {
  const hasTableRow = /^\s*\|.*\|\s*$/m.test(value);
  const hasLongPipeRun = /\|[-:\s|]{12,}\|/.test(value);

  return hasTableRow || hasLongPipeRun;
}

function DocsSearchResultCard({
  item,
}: {
  item: DocsSearchResultItem;
}): ReactNode {
  const docsLink = resolveDocsLink(item.url);
  const excerpt = getExcerpt(item);
  const openDocsLabel = 'Open docs';
  const resultLabel = `Result ${item.rank}`;
  const handleOpenDocs = async (event: MouseEvent<HTMLAnchorElement>) => {
    if (!docsLink) {
      return;
    }

    const electronShell = (window as WindowWithElectronShell).electronAPI
      ?.shell;

    if (!electronShell?.openExternal) {
      return;
    }

    event.preventDefault();
    await electronShell.openExternal(docsLink);
  };

  return (
    <VStack
      alignItems="stretch"
      gap={2}
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="sm"
      p={2}
      width="100%"
    >
      <HStack justifyContent="space-between" alignItems="start" gap={2}>
        <VStack alignItems="start" gap={0.5} minWidth={0}>
          <Text fontSize="xs" color="fg.muted">
            {resultLabel}
          </Text>
          <Text fontSize="sm" fontWeight="medium" color="fg.default" truncate>
            {item.title}
          </Text>
        </VStack>
        <Badge size="sm" variant="emphasis">
          {item.section}
        </Badge>
      </HStack>

      {item.headingPath ? (
        <Text fontSize="xs" color="fg.muted">
          {item.headingPath}
        </Text>
      ) : null}

      {excerpt ? (
        <Box
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="sm"
          bg="bg.canvas"
          p={2}
          maxH="120px"
          overflowY="auto"
        >
          {shouldRenderAsPlainText(excerpt) ? (
            <Text fontSize="xs" color="fg.default" whiteSpace="pre-wrap">
              {stripMarkdownToPlainText(excerpt)}
            </Text>
          ) : (
            <MarkdownPreview source={excerpt} fontSize={12} compact />
          )}
        </Box>
      ) : null}

      {docsLink ? (
        <HStack>
          <Link
            href={docsLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOpenDocs}
            fontSize="xs"
            display="inline-flex"
            alignItems="center"
            gap={1}
          >
            {openDocsLabel}
            <LinkSquare02Icon size={12} />
          </Link>
        </HStack>
      ) : null}
    </VStack>
  );
}

export function renderDocsSearchResult(ctx: OutputRenderContext): ReactNode {
  const { content, maxHeight } = ctx;
  const parsed = parseDocsOutput(content);

  if (!parsed) {
    if (typeof content === 'object' && content !== null) {
      return <JSONRenderer data={content} maxHeight={maxHeight} />;
    }

    return (
      <PlainTextRenderer content={String(content)} maxHeight={maxHeight} />
    );
  }

  return (
    <VStack alignItems="stretch" gap={2} width="100%">
      <HStack justifyContent="space-between" alignItems="center">
        <Text fontSize="xs" color="fg.muted">
          {parsed.query ? `Results for "${parsed.query}"` : 'Docs results'}
        </Text>
        <Badge size="sm" variant="emphasis">
          {parsed.total}
        </Badge>
      </HStack>
      <Box
        maxHeight={maxHeight ?? '300px'}
        overflowY="auto"
        overflowX="hidden"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="sm"
        p={2}
      >
        <VStack alignItems="stretch" gap={2}>
          {parsed.results.map((item) => (
            <DocsSearchResultCard
              key={`${item.slug}-${item.rank}`}
              item={item}
            />
          ))}
        </VStack>
      </Box>
    </VStack>
  );
}
